import { Metamodel } from 'app/model/base/model';
import { IViolation } from 'app/model/base/annotation';
import { Constants } from 'app/shared/shared-constants';
import { DatasetFormatType } from './dataflow-dataset-settings';
import { DataFlowExpression } from '../dataflowexpression';

import { DataFlowSource } from '../dataflow-source';
import { DataFlowSink } from '../dataflow-sink';
import { BooleanDataFlowType } from '../dataflow-datatype';
import { IntegerDataFlowType, TimeStampDataFlowType, StringDataFlowType, ArrayDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { FileBasedSettings } from './file-based-settings';
import { compressionOptionValues } from 'app/shared/components/compression-setting/compression-options';
import { isTruthy } from '../dataflow-expression-utilities';
import { ITreeNodeOptionData } from 'app/authoring/component/transformations/complex-type-utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { LoggerService } from 'app/logger/logger.service';
import { IValidationContext, LakehouseLinkedService } from 'shell-models-lib';
import { initializeDataflowSink } from 'app/model/dataflow/dataflow-utils';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export enum TimeTravelOption {
    No,
    Timestamp,
    Version
}

export enum DeltaTableActionOptions {
    None,
    Overwrite,
    Truncate,
    DeltaTruncate
}

export interface IPartitionPruningCondition {
    colName: DataFlowExpression;
    colData?: ITreeNodeOptionData;
    values: DataFlowExpression;
}

type DataflowType = DataFlowSink | DataFlowSource;

@Metamodel.model({
    name: adfModelTypeMap.delta.dataflowSettings,
    validator: DeltaSettings.validateSettings
})
export class DeltaSettings extends FileBasedSettings {
    public static datasetType = 'Delta';
    @Metamodel.attribute({
    })
    public vacuum: DataFlowExpression;
    @Metamodel.attribute({
    })
    public mergeSchema: DataFlowExpression;
    @Metamodel.attribute({
    })
    public autoCompact: DataFlowExpression;
    @Metamodel.attribute({
    })
    public optimizedWrite: DataFlowExpression;
    @Metamodel.attribute({
    })
    public versionAsOf: DataFlowExpression;
    @Metamodel.attribute({
    })
    public timestampAsOf: DataFlowExpression;
    public compressionType: string;
    @Metamodel.attribute({
    })
    public compressionLevel: DataFlowExpression;

    public tableName: DataFlowExpression;

    public tableActionOption: DeltaTableActionOptions = DeltaTableActionOptions.None;

    public timeTravelOption: TimeTravelOption = TimeTravelOption.No;

    public pruneConditions: IPartitionPruningCondition[] = [];

    private static readonly _isTridentMdf: boolean = __IS_FABRIC_MDF__;

    public static async validateSettings(settings: DeltaSettings, context: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = [];
        const transform = settings.transform as DataflowType;
        if (transform.isInlineDataset && transform.datasetSettings && transform.datasetSettings.getFormat() === DatasetFormatType.Delta) {
            // Check both schemaLinkedService (traditional) and sourceLinkedService/sinkLinkedService (MDF) for Lakehouse
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const linkedService = transform.schemaLinkedService || (transform as any).sourceLinkedService || (transform as any).sinkLinkedService;
            const isLakehouse = linkedService instanceof LakehouseLinkedService;
            
            // MDF Lakehouse Tables: Validate tableName is required
            if (DeltaSettings._isTridentMdf && isLakehouse && stringIsNullOrEmpty(settings.tableName?.expression)) {
                violations.push({
                    context: transform,
                    message: () => format(stringResources.RequiredTemplate, stringResources.Table_name_Label),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: transform, attribute: { name: 'datasetSettings' } as any }
                });
            }
            
            // Skip container validation for MDF (Lakehouse uses artifact selection, not container)
            // or when linked service is confirmed to be Lakehouse
            if (!settings.container.expression && !isLakehouse && !DeltaSettings._isTridentMdf) {
                violations.push({
                    context: transform,
                    message: () => stringResources.Dataflow_RequireContainer
                });
            } else if (!DeltaSettings._isTridentMdf && GlobalVariables.containerValidation &&
                !Constants.slashSpaceValidation.test(settings.container.expression) &&
                settings.container.expression !== settings.container.templateExpression) {
                const errorMsg = format(stringResources.Dataset_FileName_SpaceSlashValidation, stringResources.FileSystem_DisplayText);
                const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
                logger.error('DeltaSettings', errorMsg);

                violations.push({
                    context: transform,
                    message: () => errorMsg
                });
            }
            if ((settings.timeTravelOption === TimeTravelOption.Version && !settings.versionAsOf.expression)
                || (settings.timeTravelOption === TimeTravelOption.Timestamp && !settings.timestampAsOf.expression)) {
                violations.push({
                    context: transform,
                    message: () => stringResources.DataFlowSourcePanelDeltaDescriptionTimeTravelError
                });
            }
        }
        const sinkViolations = DeltaSettings.validateOptimizeSettings(settings) || [];
        violations.push(...sinkViolations);
        return violations.length > 0 ? violations : undefined;
    }

    public static validateOptimizeSettings(settings: DeltaSettings): IViolation[] {
        const violations: IViolation[] = [];
        const sink = settings.transform;
        if (sink instanceof DataFlowSource) {
            return undefined;
        }
        if (sink.isInlineDataset && sink.datasetSettings && sink.datasetSettings.getFormat() === DatasetFormatType.Delta) {
            const dataflow = sink.dataflow;
            if (dataflow && dataflow.sinks) {
                const sinks = dataflow.sinks.filter((sink) => sink && sink.datasetSettings instanceof DeltaSettings);
                if (sinks.size > 1) {
                    const iterator = sinks.values();
                    // We do know this is a sink, but we do not import the type to avoid circular dependencies.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const referenceDataset = (iterator.next().value as any).datasetSettings as DeltaSettings;
                    let cursor = iterator.next();
                    while (!cursor.done) {
                        const cursorDataset = cursor.value.datasetSettings as DeltaSettings;
                        if (this._compareExpressions(referenceDataset.autoCompact, cursorDataset.autoCompact)
                            || this._compareExpressions(referenceDataset.optimizedWrite, cursorDataset.optimizedWrite)) {
                            violations.push({
                                context: cursor.value,
                                message: () => stringResources.DataFlowSourcePanelDeltaAutoOptimizeWarning
                            });
                            break;
                        }
                        cursor = iterator.next();
                    }
                }
            }
        }
        return violations.length > 0 ? violations : undefined;
    }

    private static _compareExpressions(expA: DataFlowExpression, expB: DataFlowExpression): boolean {
        return isTruthy(expA) !== isTruthy(expB) || expA.templateExpression !== expB.templateExpression;
    }

    public get transform(): DataFlowSource | DataFlowSink {
        return this.owner instanceof DataFlowSource ? this.owner as DataFlowSource : this.owner as DataFlowSink;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.Delta;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getDatasetType(): string {
        return undefined;
    }

    public getIconClass(): string {
        return 'i-20x20-delta-lake';
    }

    public initialize(subFormat: string): void {
        super.initialize(subFormat);
        this.allowInsert = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.allowUpsert = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.allowDelete = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.allowUpdate = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.keyColumns = [this.transform.createTemplatableExpression()];
        initializeDataflowSink(this);
        this.pruneConditions = [{
            colName: this.transform.createTemplatableExpression(),
            values: this.transform.createTemplatableExpression(ArrayDataFlowType.TYPE)
        }];
        this.mergeSchema = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.autoCompact = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.optimizedWrite = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.vacuum = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE, '0');
        this.timestampAsOf = this.transform.createTemplatableExpression(TimeStampDataFlowType.TYPE);
        this.versionAsOf = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE);
        this.compressionLevel = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, compressionOptionValues.Fastest);
        this.tableName = this.transform.createTemplatableExpression(StringDataFlowType.TYPE);
    }
}
